import { useState, useEffect } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { PlanGate } from "@/components/enterprise-gate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { format } from "date-fns";
import { useLocation } from "wouter";
import { useBranch } from "@/context/branch";
import { readJsonArray } from "@/lib/http";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function fmt(v: number) {
  return `₦${v.toLocaleString("en-NG", { minimumFractionDigits: 2 })}`;
}

const METHODS = ["cash", "card", "transfer", "cheque", "other"];
const TYPE_BADGE: Record<string, string> = {
  received: "bg-emerald-100 text-emerald-800",
  made: "bg-red-100 text-red-800",
};

export default function AccountingPayments() {
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const { activeBranchId, activeBranch } = useBranch();
  const [typeFilter, setTypeFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (location.includes("add=1")) setIsOpen(true);
  }, [location]);

  const [form, setForm] = useState({
    type: "received",
    amount: "",
    method: "cash",
    reference: "",
    description: "",
    paidBy: "",
    date: new Date().toISOString().split("T")[0],
  });
  const [formError, setFormError] = useState("");

  const { data: payments, isLoading } = useQuery({
    queryKey: ["accounting-payments", typeFilter, startDate, endDate, activeBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (typeFilter) p.set("type", typeFilter);
      if (startDate) p.set("startDate", startDate);
      if (endDate) p.set("endDate", endDate);
      if (activeBranchId) p.set("branchId", String(activeBranchId));
      const res = await fetch(`${BASE}/api/accounting/payments?${p}`, { credentials: "include" });
      return readJsonArray<any>(res, "payments");
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await fetch(`${BASE}/api/accounting/payments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...data, amount: Number(data.amount), branchId: activeBranchId ?? undefined }),
      });
      if (!res.ok) throw new Error("Failed to create payment");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["accounting-payments"] });
      setIsOpen(false);
      setForm({ type: "received", amount: "", method: "cash", reference: "", description: "", paidBy: "", date: new Date().toISOString().split("T")[0] });
    },
    onError: () => setFormError("Failed to save payment."),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/accounting/payments/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete payment");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["accounting-payments"] }),
    onError: () => setFormError("Failed to delete payment."),
  });

  const totalReceived = payments?.filter(p => p.type === "received").reduce((s: number, p: any) => s + p.amount, 0) ?? 0;
  const totalMade = payments?.filter(p => p.type === "made").reduce((s: number, p: any) => s + p.amount, 0) ?? 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    if (!form.description.trim()) { setFormError("Description is required"); return; }
    if (!form.amount || Number(form.amount) <= 0) { setFormError("Amount must be greater than 0"); return; }
    createMutation.mutate(form);
  };

  return (
    <PlanGate allowedPlans={["pro", "enterprise"]} feature="Payments">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Payments — {activeBranch?.name ?? "All Branches"}</h1>
          <p className="text-muted-foreground mt-1">Track money received and payments made for the selected location.</p>
        </div>
        <Button onClick={() => setIsOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Record Payment
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Received</span>
              <TrendingUp className="w-4 h-4 text-emerald-500" />
            </div>
            <div className="text-xl font-bold text-emerald-600">{fmt(totalReceived)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-400">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Total Paid Out</span>
              <TrendingDown className="w-4 h-4 text-red-400" />
            </div>
            <div className="text-xl font-bold text-red-500">{fmt(totalMade)}</div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm text-muted-foreground">Net Position</span>
              <Wallet className="w-4 h-4 text-blue-500" />
            </div>
            <div className={`text-xl font-bold ${totalReceived - totalMade >= 0 ? "text-blue-600" : "text-destructive"}`}>
              {fmt(totalReceived - totalMade)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="made">Made</SelectItem>
          </SelectContent>
        </Select>
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-40" />
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-40" />
        {(typeFilter || startDate || endDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setTypeFilter(""); setStartDate(""); setEndDate(""); }}>Clear</Button>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>From / To</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : !payments?.length ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No payments recorded yet</TableCell></TableRow>
            ) : [...payments].reverse().map((p: any) => (
              <TableRow key={p.id}>
                <TableCell className="text-sm">{format(new Date(p.date), "MMM d, yyyy")}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_BADGE[p.type]}`}>
                    {p.type === "received" ? "↓ Received" : "↑ Made"}
                  </span>
                </TableCell>
                <TableCell className="font-medium">{p.description}</TableCell>
                <TableCell className="text-muted-foreground">{p.paidBy || "—"}</TableCell>
                <TableCell><Badge variant="secondary" className="capitalize">{p.method}</Badge></TableCell>
                <TableCell className="text-muted-foreground text-sm">{p.reference || "—"}</TableCell>
                <TableCell className={`text-right font-semibold ${p.type === "received" ? "text-emerald-600" : "text-red-500"}`}>
                  {p.type === "received" ? "+" : "−"}{fmt(p.amount)}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteMutation.mutate(p.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Record Payment Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Record Payment</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={form.type} onValueChange={v => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="received">Money Received</SelectItem>
                    <SelectItem value="made">Payment Made</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Amount (₦)</label>
                <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} className="mt-1" placeholder="0.00" />
              </div>
              <div>
                <label className="text-sm font-medium">Method</label>
                <Select value={form.method} onValueChange={v => setForm(p => ({ ...p, method: v }))}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHODS.map(m => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Date</label>
                <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="mt-1" placeholder="e.g. Customer deposit, Supplier payment..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{form.type === "received" ? "Received From" : "Paid To"}</label>
                <Input value={form.paidBy} onChange={e => setForm(p => ({ ...p, paidBy: e.target.value }))} className="mt-1" placeholder="Name or company" />
              </div>
              <div>
                <label className="text-sm font-medium">Reference (optional)</label>
                <Input value={form.reference} onChange={e => setForm(p => ({ ...p, reference: e.target.value }))} className="mt-1" placeholder="Invoice #, receipt #..." />
              </div>
            </div>
            {formError && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded">{formError}</p>}
            <Button type="submit" className="w-full" disabled={createMutation.isPending}>
              {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Payment
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
    </PlanGate>
  );
}
